/* eslint-disable no-param-reassign */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'store';
import {
  StateSlice,
  initialStateSlice,
  startLoading,
  loadingFailed
} from 'store/slices';
import {
  capacityTypeDomain,
  geographicAreaDomain,
  reserveCapacityDomain
} from 'models';
import http from 'api/http';
import { handleThunkError } from 'store/utils/errors';
import ReserveCapacity, {
  EditReserveCapacityRequest
} from 'models/ReserveCapacity';
import { addItemThunkNew, editItemThunkNew, createUrl } from 'store/utils';
import { GeographicArea } from 'models/GeographicArea';
import CapacityType from 'models/CapacityType';
import { FormValues } from 'components/Forms';
import { deleteItemThunk } from 'store/utils/';
import { DomainItem } from 'models/Domain';
import { setSnackbar, SnackbarStatus } from './snackbar';

const sortByReserveCapacityStatus = (
  a: ReserveCapacity,
  b: ReserveCapacity
) => {
  if (!a.status || !b.status) {
    return 0;
  }
  return a.status.localeCompare(b.status);
};

const reservedCapacity = createSlice({
  name: reserveCapacityDomain.plural,
  initialState: initialStateSlice,
  reducers: {
    ...startLoading<ReserveCapacity>(),
    ...loadingFailed<ReserveCapacity>(),
    getReserveCapacitiesSuccess(
      state: StateSlice<ReserveCapacity>,
      { payload: items }: PayloadAction<ReserveCapacity[]>
    ) {
      state.items = [...items].sort(sortByReserveCapacityStatus);
      state.isLoading = false;
      state.error = null;
    },
    editReserveCapacitySuccess(state: StateSlice<ReserveCapacity>) {
      state.isLoading = false;
      state.error = null;
    },
    addReserveCapacitySuccess(state: StateSlice<ReserveCapacity>) {
      state.isLoading = false;
      state.error = null;
    },
    deleteReserveCapacitySuccess(
      state: StateSlice<ReserveCapacity>,
      { payload: deletedReserveCapacityId }: PayloadAction<string>
    ) {
      if (state.items != null) {
        state.items = state.items.filter(
          item => item.id !== deletedReserveCapacityId
        );
      }
      state.isLoading = false;
      state.error = null;
    }
  }
});

export const {
  requestStart,
  requestFail,
  getReserveCapacitiesSuccess,
  addReserveCapacitySuccess,
  editReserveCapacitySuccess,
  deleteReserveCapacitySuccess
} = reservedCapacity.actions;

export default reservedCapacity.reducer;

export const reserveCapacitySelector = createSelector(
  [
    state => state[reserveCapacityDomain.plural].items as ReserveCapacity[],
    state => state[geographicAreaDomain.plural].items as GeographicArea[],
    state => state[capacityTypeDomain.plural].items as CapacityType[]
  ],
  (reserveCapacity, geographicAreas, capacityTypes) =>
    reserveCapacity.map(rc => {
      const { area, capacityType } = rc;

      return {
        ...rc,
        area:
          geographicAreas.find(({ id: areaId }) => areaId === area)?.name ??
          area,
        capacityType:
          capacityTypes.find(
            ({ id: capacityTypeId }) => capacityTypeId === capacityType
          )?.name ?? capacityType
      };
    })
);

export function fetchReserveCapacities(): AppThunk {
  return async dispatch => {
    const url = createUrl(reserveCapacityDomain);
    try {
      dispatch(requestStart());

      const { data } = await http.get<ReserveCapacity[]>(url);

      dispatch(getReserveCapacitiesSuccess(data));
    } catch (error) {
      dispatch(
        setSnackbar({
          message: 'Something went wrong. Try again or refresh page',
          show: true,
          status: SnackbarStatus.Error,
          useNewSnackbar: true
        })
      );

      handleThunkError({
        error,
        requestFail,
        dispatch,
        domainType: reserveCapacityDomain.type,
        displayError: false
      });
    }
  };
}

export function editReserveCapacity(
  item: EditReserveCapacityRequest
): AppThunk {
  return editItemThunkNew({
    domain: reserveCapacityDomain,
    requestStart,
    requestFail,
    requestSuccess: editReserveCapacitySuccess,
    item
  });
}

export function addReserveCapacity(item: FormValues): AppThunk | any {
  return addItemThunkNew({
    domain: reserveCapacityDomain,
    requestStart,
    requestFail,
    requestSuccess: addReserveCapacitySuccess,
    item,
    successMessage: 'New Capacity has been successfully added.'
  });
}

export function deleteReserveCapacity(domainItem: DomainItem): AppThunk {
  return deleteItemThunk<ReserveCapacity>({
    domain: reserveCapacityDomain,
    requestStart,
    requestFail,
    requestSuccess: deleteReserveCapacitySuccess,
    itemId: domainItem.id,
    deleteMessage: 'Capacity successfully deleted',
    useNewSnackbar: true,
    snackbarStatus: SnackbarStatus.Success
  });
}
